// The parent's half of `useData`: the datasets a fence's UI reads, the query
// results it is sent, and where a fence may read datasets at all.
//
// Promise:
// - The rows stay here; a UI gets only its query's result, at most
//   DATASET_LIMITS.toFrame rows (beyond that `truncated`).
// - Every UI on the screen reading the same dataset shares one copy and one
//   sync (datasets/queries.ts).
// - Where the host says so (a Vault's screens, `extensions.datasets.queryHere`)
//   no copy is kept: each distinct query is worked out where the dataset is
//   and only its result comes, asked again on the dataset's notices.
// - A result is worked out again only when its dataset's copy changed, and
//   sent only when it differs from what the UI already has.
// - Where a fence may read (`UiDataScope`): a personal document and an Agent
//   chat read any dataset the viewer sees; a Space document reads none that
//   is personal (its readers are others); a place without a scope (the field
//   below a chat, a public page) reads none (`unavailable`). A dataset the
//   viewer cannot see answers `not_found`, never why.
import { useQueries, useQueryClient } from "@tanstack/react-query";
import {
  checkQuery,
  DATASET_LIMITS,
  type DatasetQueryAnswer,
  type DatasetRow,
  runQuery,
} from "@trameria/core/datasets-shared";
import type { UiDataState, UiDataWant } from "@trameria/ui-runtime/protocol";
import { createContext, useMemo, useRef } from "react";
import { useMeApp } from "../app/context";
import { ApiError } from "../data/api";
import { datasetCopies } from "../datasets/copies";
import { datasetQueryQuery, datasetSyncQuery } from "../datasets/queries";
import type { DatasetCopy } from "../datasets/sync";

/** Where a fence is, as far as reading datasets goes. */
export type UiDataScope = "personal" | "space" | "chat";

/**
 * The scope of the fences below (a document screen says whether it shows a
 * personal or a Space document). A place's own `dataScope` wins.
 */
export const UiDataScopeContext = createContext<UiDataScope | null>(null);

export const DATA_ERRORS = {
  notFound: "not_found",
  unavailable: "unavailable",
  failed: "failed",
  invalidQuery: "invalid_query",
} as const;

const MAX_WANTS = 20;
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The wants of a `dataWant` message (malformed ones dropped). */
export function readDataWants(raw: unknown): UiDataWant[] {
  if (!Array.isArray(raw)) return [];
  const out: UiDataWant[] = [];
  for (const w of raw.slice(0, MAX_WANTS)) {
    if (!w || typeof w !== "object") continue;
    const { key, dataset, query } = w as Record<string, unknown>;
    if (typeof key !== "string" || typeof dataset !== "string") continue;
    out.push({ key, dataset, ...(query === undefined ? {} : { query }) });
  }
  return out;
}

const NOT_READY: UiDataState = {
  ready: false,
  rows: [],
  schema: null,
  truncated: false,
  error: null,
};

const failed = (error: string): UiDataState => ({
  ready: true,
  rows: [],
  schema: null,
  truncated: false,
  error,
});

/** The dataset id a fence may ask for here, or null. */
export const readableDataset = (dataset: string, scope: UiDataScope | null) =>
  scope !== null && UUID_RE.test(dataset) ? dataset.toLowerCase() : null;

/** Whether a fence in `scope` may read the dataset of this copy. */
export function scopeReads(scope: UiDataScope, copy: DatasetCopy): boolean {
  return !(scope === "space" && copy.dto?.place.kind === "personal");
}

const NO_ROWS: ReadonlyMap<string, DatasetRow> = new Map();

/** What a UI gets for one want worked out where the dataset is. */
export function remoteDataResult(
  want: UiDataWant,
  scope: UiDataScope | null,
  answer: DatasetQueryAnswer | undefined,
  error: unknown,
): UiDataState {
  if (scope === null) return failed(DATA_ERRORS.unavailable);
  if (readableDataset(want.dataset, scope) === null)
    return failed(DATA_ERRORS.notFound);
  const query = checkQuery(want.query);
  if (!query.ok) return failed(`${DATA_ERRORS.invalidQuery}: ${query.error}`);
  if (error instanceof ApiError && error.status === 404)
    return failed(DATA_ERRORS.notFound);
  if (!answer) return error ? failed(DATA_ERRORS.failed) : NOT_READY;
  if (scope === "space" && answer.dataset.place.kind === "personal")
    return failed(DATA_ERRORS.notFound);
  return {
    ready: true,
    rows: answer.rows,
    schema: answer.dataset.schema,
    truncated: answer.truncated,
    error: null,
  };
}

/** What a UI gets for one want, from the copy's state. */
export function dataResult(
  want: UiDataWant,
  scope: UiDataScope | null,
  copy: DatasetCopy | undefined,
  error: unknown,
): UiDataState {
  if (scope === null) return failed(DATA_ERRORS.unavailable);
  if (readableDataset(want.dataset, scope) === null)
    return failed(DATA_ERRORS.notFound);
  const query = checkQuery(want.query);
  if (!query.ok) return failed(`${DATA_ERRORS.invalidQuery}: ${query.error}`);
  if (error instanceof ApiError && error.status === 404)
    return failed(DATA_ERRORS.notFound);
  if (!copy) return error ? failed(DATA_ERRORS.failed) : NOT_READY;
  if (!scopeReads(scope, copy)) return failed(DATA_ERRORS.notFound);
  const out = runQuery(copy.rows.values(), query.query, {
    timezone: copy.dto?.timezone ?? "UTC",
    schema: copy.dto?.schema ?? null,
    max: DATASET_LIMITS.toFrame,
  });
  return {
    ready: true,
    rows: out.rows,
    schema: copy.dto?.schema ?? null,
    truncated: out.truncated,
    error: null,
  };
}

/** One dataset a fence reads, as the "Dataset" panel under the UI shows it. */
export interface FenceDataRead {
  /** The id as the UI first wrote it. */
  dataset: string;
  /** False when it cannot be read here (only the id is shown, never why). */
  found: boolean;
  /** Its name, once its copy is here. */
  title: string | null;
  /**
   * What opens its screen beyond the id (a workspace's slug; nothing for a
   * personal one), or null until its copy is here.
   */
  path: { workspace?: string } | null;
  /** The distinct queries the UI asks it, in order; null is all rows. */
  queries: unknown[];
}

const noQuery = (q: unknown) =>
  q === undefined ||
  q === null ||
  (typeof q === "object" && !Array.isArray(q) && Object.keys(q).length === 0);

/** The datasets of a fence's wants (from what useFenceData already holds). */
export function fenceDataReads(
  reads: ReadonlyArray<{
    want: UiDataWant;
    copy: DatasetCopy | undefined;
    state: UiDataState;
  }>,
): FenceDataRead[] {
  const out = new Map<string, FenceDataRead & { seen: Set<string> }>();
  for (const { want, copy, state } of reads) {
    const id = UUID_RE.test(want.dataset)
      ? want.dataset.toLowerCase()
      : want.dataset;
    let read = out.get(id);
    if (!read) {
      read = {
        dataset: want.dataset,
        found: true,
        title: null,
        path: null,
        queries: [],
        seen: new Set(),
      };
      out.set(id, read);
    }
    if (
      state.error === DATA_ERRORS.notFound ||
      state.error === DATA_ERRORS.unavailable
    )
      read.found = false;
    else if (copy?.dto) {
      read.title = copy.dto.title;
      const place = copy.dto.place;
      // A personal one opens beside the person's documents (no path to say).
      read.path = place.kind === "space" ? { workspace: place.workspace } : {};
    }
    const query = noQuery(want.query) ? null : want.query;
    const json = JSON.stringify(query);
    if (read.seen.has(json)) continue;
    read.seen.add(json);
    read.queries.push(query);
  }
  return [...out.values()].map(({ seen: _, ...read }) =>
    read.found ? read : { ...read, title: null, path: null },
  );
}

/** JSON on one line, with a space after `,` and `:` (as people write it). */
function inlineJson(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(inlineJson).join(", ")}]`;
  if (v && typeof v === "object")
    return `{${Object.entries(v)
      .map(([k, x]) => `${JSON.stringify(k)}: ${inlineJson(x)}`)
      .join(", ")}}`;
  return JSON.stringify(v) ?? "null";
}

const QUERY_LINE = 60;

/** A query as JSON to read: a part that fits on a line stays on one. */
export function formatQuery(q: unknown, indent = ""): string {
  const line = inlineJson(q);
  if (indent.length + line.length <= QUERY_LINE || !q || typeof q !== "object")
    return line;
  const inner = `${indent}  `;
  if (Array.isArray(q))
    return `[\n${q.map((x) => inner + formatQuery(x, inner)).join(",\n")}\n${indent}]`;
  return `{\n${Object.entries(q)
    .map(([k, x]) => `${inner}${JSON.stringify(k)}: ${formatQuery(x, inner)}`)
    .join(",\n")}\n${indent}}`;
}

interface Held {
  copy: DatasetCopy | undefined;
  /** Worked out where the dataset is (no copy then). */
  answer?: DatasetQueryAnswer;
  error: unknown;
  scope: UiDataScope | null;
  state: UiDataState;
}

export interface FenceData {
  /** The result of each want, by key. */
  results: ReadonlyMap<string, UiDataState>;
  /** The datasets the wants read. */
  reads: FenceDataRead[];
}

/**
 * The results of a fence's wants, by key, and the datasets they read. Keeps
 * the copies it reads synced while the fence is mounted.
 */
export function useFenceData(
  wants: readonly UiDataWant[],
  scope: UiDataScope | null,
): FenceData {
  const { api, extensions } = useMeApp();
  const here = extensions.datasets?.queryHere === true;
  const qc = useQueryClient();
  const deps = useMemo(() => ({ api, qc, copies: datasetCopies() }), [api, qc]);
  const ids = useMemo(
    () =>
      here
        ? []
        : [
            ...new Set(
              wants.flatMap((w) => readableDataset(w.dataset, scope) ?? []),
            ),
          ],
    [wants, scope, here],
  );
  const queries = useQueries({
    queries: ids.map((id) => datasetSyncQuery(deps, id)),
  });
  // Where the queries are worked out by the dataset: one per distinct query.
  const asks = useMemo(() => {
    if (!here) return [];
    const out = new Map<string, { id: string; query: string }>();
    for (const w of wants) {
      const id = readableDataset(w.dataset, scope);
      if (id === null || !checkQuery(w.query).ok) continue;
      const query = JSON.stringify(w.query ?? null);
      out.set(`${id} ${query}`, { id, query });
    }
    return [...out.values()];
  }, [wants, scope, here]);
  const answers = useQueries({
    queries: asks.map((a) => datasetQueryQuery(api, a.id, a.query)),
  });
  const held = useRef(new Map<string, Held>());
  const out = new Map<string, UiDataState>();
  const next = new Map<string, Held>();
  const reads: Array<{
    want: UiDataWant;
    copy: DatasetCopy | undefined;
    state: UiDataState;
  }> = [];
  for (const want of wants) {
    const id = readableDataset(want.dataset, scope);
    if (here) {
      const query = JSON.stringify(want.query ?? null);
      const i = asks.findIndex((a) => a.id === id && a.query === query);
      const q = i >= 0 ? answers[i] : undefined;
      const answer = q?.data;
      const error = q?.error ?? null;
      const before = held.current.get(want.key);
      const same =
        before &&
        before.answer === answer &&
        before.error === error &&
        before.scope === scope;
      const state = same
        ? before.state
        : remoteDataResult(want, scope, answer, error);
      // The panel reads the name and place as from a copy (no rows here).
      const copy: DatasetCopy | undefined = answer
        ? {
            id: answer.dataset.id,
            seq: answer.seq,
            rev: answer.dataset.rev,
            dto: answer.dataset,
            rows: NO_ROWS,
          }
        : undefined;
      next.set(want.key, { copy: undefined, answer, error, scope, state });
      out.set(want.key, state);
      reads.push({ want, copy, state });
      continue;
    }
    const q = id === null ? undefined : queries[ids.indexOf(id)];
    const copy = q?.data;
    const error = q?.error ?? null;
    const before = held.current.get(want.key);
    const same =
      before &&
      before.copy === copy &&
      before.error === error &&
      before.scope === scope;
    const state = same ? before.state : dataResult(want, scope, copy, error);
    next.set(want.key, { copy, error, scope, state });
    out.set(want.key, state);
    reads.push({ want, copy, state });
  }
  held.current = next;
  return { results: out, reads: fenceDataReads(reads) };
}

/**
 * The data messages to send now: those whose result differs from what was
 * sent (compared by content, so a sync that changed nothing a UI shows
 * sends nothing). `sent` is updated.
 */
export function dataMessagesToSend(
  results: ReadonlyMap<string, UiDataState>,
  sent: Map<string, { state: UiDataState; json: string }>,
): Array<UiDataState & { key: string }> {
  const out: Array<UiDataState & { key: string }> = [];
  for (const [key, state] of results) {
    const last = sent.get(key);
    if (last?.state === state) continue;
    const json = JSON.stringify(state);
    sent.set(key, { state, json });
    if (last?.json === json) continue;
    out.push({ key, ...state });
  }
  return out;
}
